import type { Dispatch, PropsWithChildren, SetStateAction } from "react";
import { createContext } from "react";
import type { HtmlEditorUploadedImage } from "@entrilia/shared/components/html/htmlEditorTypes";
import type { Category } from "../../../../api/types/accessTypes";
import type {
  InvestorCommunication,
  InvestorCommunicationEngagement,
  InvestorCommunicationReader,
  InvestorCommunicationRecipient,
} from "../../../../api/types/investorCommunicationsTypes";
import type { LookupObjectReference } from "../../../../api/types/objectTypes";
import type { InvestorCommunicationStatusTransition } from "../investorCommunicationStatusHelper";
import type { DetailsPageTab, CommunicationImageSource, SaveAction } from "./investorCommunicationDetailsTypes";
import type {
  InvestorCommunicationEditState,
  InvestorCommunicationValidationErrors,
} from "./investorCommunicationEditState";

interface Props {
  investorCommunication: InvestorCommunication;
  recipients: InvestorCommunicationRecipient[];
  engagement?: InvestorCommunicationEngagement;
  readersByContactId?: Map<string, InvestorCommunicationReader>;
  funds: LookupObjectReference[];
  categories: Category[];
  hasEditPermissions: boolean;
  isNewsVisibleToInvestors: boolean;
  savedHeroImageSource?: CommunicationImageSource;
  draftHeroImageSource?: CommunicationImageSource;
  savedThumbnailSource?: CommunicationImageSource;
  draftThumbnailSource?: CommunicationImageSource;
  draftVideoUrl?: string;
  videoUploadProgress?: number;
  thumbnailCaptureError?: string;
  isGeneratingThumbnail: boolean;
  state: InvestorCommunicationEditState;
  setState: Dispatch<SetStateAction<InvestorCommunicationEditState>>;
  hasChanges: boolean;
  isSaveAllowed: boolean;
  validationErrors: InvestorCommunicationValidationErrors;
  isSaving: boolean;
  hasPendingImageUploads: boolean;
  pendingSaveAction?: SaveAction;
  selectedTab: DetailsPageTab;
  onTabChange: (tab: DetailsPageTab) => void;
  onStatusChange: (statusTransition: InvestorCommunicationStatusTransition) => void;
  onDelete: () => void;
  onDiscardChanges: () => void;
  onSave: () => void;
  onSaveAndExit: () => void;
  onHeroImageFileAdd: (file: File) => void;
  onHeroImageRemove: () => void;
  onVideoFileAdd: (file: File) => void;
  onVideoFileRemove: () => void;
  onThumbnailFileAdd: (file: File) => void;
  onThumbnailFileRemove: () => void;
  onBodyImagesUpload: (files: File[]) => Promise<HtmlEditorUploadedImage[]>;
}

const InvestorCommunicationContext = createContext<Props | undefined>(undefined);

export const InvestorCommunicationContextProvider = ({ children, ...contextValue }: PropsWithChildren<Props>) => (
  <InvestorCommunicationContext value={contextValue}>{children}</InvestorCommunicationContext>
);

export default InvestorCommunicationContext;
